import {
  AttemptType,
  BrcodeType,
  ContractReasonCancellation,
  RefundStatus,
} from '@/app/shared/enums';
import {
  AttemptModel,
  CardChangerModel,
  ComplementaryModel,
  ContactBillingModel,
  ContractModel,
  CustomerBillingModel,
  InstallmentModel,
  PaymentInfoModel,
  PaymentMethodAvaiable,
  RefundGroupModel,
  RefundModel,
  RefundSummaryResponseModel,
  StatementProcessorResponseModel,
  TransactionModel,
  WebhookEventModel,
  WebhookEventResendModel,
} from '@/app/shared/models';
import { HttpService } from '@/app/shared/services/http.service';
import { env } from '@/environments';
import { refsService } from '../../refs.service';

export class ContractService extends HttpService {
  // public async loadContractList(
  //   Filter?: string,
  //   limit = 999
  // ): Promise<ContractModel[]> {
  //   return await this.get<ContractModel[]>(`${env.billingUri}/Contracts`, {
  //     params: { Filter, limit },
  //     headers: { tenantId: refsService.modules.company.account.Id },
  //   }).then(({ data }) => data);
  // }

  private get refs() {
    return refsService;
  }
  public async getContractById(contractId: string): Promise<ContractModel> {
    return await this.get<ContractModel>(
      `${env.billingUri}/Contracts/${contractId}`,
      { headers: { tenantId: refsService.modules.company.account.Id } }
    ).then(({ data }) => data);
  }
  public async updateContract(
    contractId: string,
    contract: ContractModel,
    descReason: string
  ): Promise<void> {
    await this.put<void, ContractModel>(
      `${env.billingUri}/Contracts/${contractId}/basic`,
      contract,
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    );
  }
  public async cancelContract(data: {
    contractId: string;
    ReasonForCancellation: ContractReasonCancellation;
    descReason: string;
  }): Promise<void> {
    await this.post(
      `${env.billingUri}/Contracts/${data.contractId}/cancel`,
      {
        ReasonForCancellation:
          ContractReasonCancellation[data.ReasonForCancellation],
      },
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(data.descReason),
        },
      }
    );
  }
  public async reactivateContract(
    contractId: string,
    descReason: string,
    generateCharge: boolean,
    installments: InstallmentModel[]
  ): Promise<void> {
    await this.post(
      `${env.billingUri}/Contracts/${contractId}/reactivate`,
      installments,
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
          generateCharge: generateCharge,
        },
      }
    );
  }

  public async getCustomerByContractId(
    contractId: string
  ): Promise<CustomerBillingModel[]> {
    return await this.get<CustomerBillingModel[]>(
      `${env.billingUri}/Contracts/${contractId}/customers`,
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          'Accept-Language': 'pt-BR,pt;',
        },
      }
    ).then(({ data }) => data);
  }
  public async updateCustomer(
    contractId: string,
    customerId: string,
    customer: CustomerBillingModel,
    descReason: string
  ): Promise<void> {
    await this.put<void, CustomerBillingModel>(
      `${env.billingUri}/Contracts/${contractId}/customers/${customerId}`,
      customer,
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    );
  }

  public async getComplementaryByContractId(
    contractId: string,
    limit = 999
  ): Promise<ComplementaryModel[]> {
    return await this.get<ComplementaryModel[]>(
      `${env.billingUri}/Contracts/${contractId}/complementaries`,
      {
        params: { limit },
        headers: {
          tenantId: refsService.modules.company.account.Id,
          'Accept-Language': 'pt-BR,pt;',
        },
      }
    ).then(({ data }) => data);
  }

  public async updateComplementary(
    contractId: string,
    complementaryId: string,
    complementary: ComplementaryModel,
    descReason: string
  ): Promise<void> {
    await this.put<void, ComplementaryModel>(
      `${env.billingUri}/Contracts/${contractId}/complementaries/${complementaryId}`,
      complementary,
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    );
  }

  public async removeComplementary(
    contractId: string,
    complementaryId: string,
    descReason: string
  ): Promise<void> {
    await this.delete(
      `${env.billingUri}/Contracts/${contractId}/complementaries/${complementaryId}`,
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    );
  }

  public async addComplementary(
    contractId: string,
    complementary: ComplementaryModel,
    descReason: string
  ): Promise<void> {
    await this.post<void, ComplementaryModel[]>(
      `${env.billingUri}/Contracts/${contractId}/complementaries`,
      [complementary],
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    );
  }

  // public async getPaymentInfoBytId(
  //   contractId: string,
  //   paymentInfoId: string
  // ): Promise<PaymentInfoModel[]> {
  //   return await this.get<PaymentInfoModel[]>(
  //     `${env.billingUri}/Contracts/${contractId}/paymentInfos/${paymentInfoId}`,
  //     { headers: { tenantId: refsService.modules.company.account.Id } }
  //   ).then(({ data }) => data);
  // }
  public async getPaymentInfoByContractId(
    contractId: string
  ): Promise<PaymentInfoModel[]> {
    return await this.get<PaymentInfoModel[]>(
      `${env.billingUri}/Contracts/${contractId}/paymentInfos`,
      { headers: { tenantId: refsService.modules.company.account.Id } }
    ).then(({ data }) => data);
  }
  public async getPaymentInfoByInstallmentId(
    contractId: string,
    installmentId: string
  ): Promise<PaymentInfoModel[]> {
    return await this.get<PaymentInfoModel[]>(
      `${env.billingUri}/Contracts/${contractId}/installment/${installmentId}/paymentInfos`,
      { headers: { tenantId: refsService.modules.company.account.Id } }
    ).then(({ data }) => data);
  }
  public async addCard(
    card: CardChangerModel
  ): Promise<{ ok: boolean; cardKey: string }> {
    return await this.post<{ ok: boolean; cardKey: string }, object>(
      `${env.changerUri}/Card/addcard/`,
      card
    ).then(({ data }) => data);
  }
  public async updatePaymentInfoContract(
    contractId: string,
    paymentInfo: PaymentInfoModel[],
    descReason: string
  ): Promise<void> {
    return await this.put<void, PaymentInfoModel[]>(
      `${env.billingUri}/Contracts/${contractId}/paymentInfos/`,
      paymentInfo,
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    ).then(({ data }) => data);
  }

  public async updatePaymentInfoParcel(
    contractId: string,
    paymentInfo: PaymentInfoModel[],
    installmentId: string,
    descReason: string
  ): Promise<void> {
    return await this.put<void, PaymentInfoModel[]>(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}/paymentInfos`,
      paymentInfo,
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    ).then(({ data }) => data);
  }

  public async updatePaymentMethod(
    contractId: string,
    installmentId: string,
    paymentInfo: PaymentInfoModel[],
    descReason: string
  ): Promise<void> {
    return await this.put<void, PaymentInfoModel[]>(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}/paymentMethods`,
      paymentInfo,
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    ).then(({ data }) => data);
  }
  // public async createCreditCard(creditCard: object): Promise<{ Id: string }> {
  //   return await this.post<{ Id: string }, object>(
  //     `${env.cardUri}/Cards`,
  //     creditCard,
  //     { headers: { tenantId: refsService.modules.company.account.Id } }
  //   ).then(({ data }) => data);
  // }

  public async getInstallmentList(
    contractId: string,
    filter?: string,
    limit = 999
  ): Promise<InstallmentModel[]> {
    return await this.get<InstallmentModel[]>(
      `${env.billingUri}/Contracts/${contractId}/installments`,
      {
        params: { limit, filter },
      }
    ).then(({ data }) => data);
  }

  public async getInstallmentChargebackHistory(
    contractId: string,
    installmentId: string,
    filter?: string,
    limit = 999
  ): Promise<InstallmentModel[]> {
    return await this.get<InstallmentModel>(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}`,
      {
        params: { limit, filter },
      }
    ).then(({ data }) => data.ChargeBackHistory as InstallmentModel[]);
  }

  public async getRefundGroup(
    refundGroupId: string
  ): Promise<RefundGroupModel> {
    const refundGroup = await this.get<RefundGroupModel>(
      `${env.financialUri}/RefundGroup/${refundGroupId}`
    ).then(({ data }) => data);
    if (refundGroup) {
      refundGroup.ContractName = refundGroup.Name;
      if (
        refundGroup.Refunds &&
        refundGroup.Refunds[0].Statements &&
        refundGroup.Refunds[0].Statements[0].ContractId
      ) {
        const contract = await this.loadContract(
          refundGroup.Refunds[0].Statements[0].ContractId
        );
        if (contract.Customers) {
          refundGroup.ContractName = contract.Customers[0].Name;
        }
      }
    }
    return refundGroup;
  }

  public async getPaymentMethodAvaiable(
    filter?: string,
    limit = 999
  ): Promise<PaymentMethodAvaiable[]> {
    return await this.get<PaymentMethodAvaiable[]>(
      `${env.billingUri}/Settings/charges?` + filter,
      {
        params: { limit },
        headers: { tenantId: refsService.modules.company.account.Id },
      }
    ).then(({ data }) => data);
  }

  public async getRefundList(
    filter?: string,
    limit = 999
  ): Promise<RefundModel[]> {
    return await this.get<RefundModel[]>(`${env.financialUri}/Refund/`, {
      params: { limit, filter },
    }).then(({ data }) => data);
  }
  public async getRefundListByContract(
    contractNumber: number
  ): Promise<RefundModel[]> {
    return await this.get<RefundModel[]>(
      `${env.financialUri}/Refund/contract/${contractNumber}`
    ).then(({ data }) => data);
  }
  public async getRefundGroupList(
    Filter?: string,
    limit = 999
  ): Promise<RefundGroupModel[]> {
    return await this.get<RefundGroupModel[]>(
      `${env.financialUri}/RefundGroup?` + Filter,
      {
        params: { limit },
      }
    ).then(({ data }) => data);
  }
  public async createInstallment(
    contractId: string,
    installment: InstallmentModel
  ): Promise<{ Id: string }> {
    return await this.post<{ Id: string }, InstallmentModel>(
      `${env.billingUri}/Contracts/${contractId}/installments`,
      installment,
      { headers: { tenantId: refsService.modules.company.account.Id } }
    ).then(({ data }) => data);
  }
  public async createAttempt(
    contractId: string,
    installmentId: string,
    date: string | Date | undefined
  ): Promise<{ Id: string }> {
    return await this.post<{ Id: string }, object>(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}/attempt`,
      {
        ExecutionDate: date,
        Type: AttemptType.ManualCharge,
      },
      { headers: { tenantId: refsService.modules.company.account.Id } }
    ).then(({ data }) => data);
  }
  public async updateInstallment(
    contractId: string,
    installmentId: string,
    installment: InstallmentModel,
    descReason: string
  ): Promise<void> {
    return await this.put<void, InstallmentModel>(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}`,
      installment,
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    ).then(({ data }) => data);
  }

  public async defaulterInstallment(
    contractId: string,
    installmentId: string,
    descReason: string
  ): Promise<void> {
    await this.post(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}/defaulter`,
      {},
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    );
  }
  public async updateRefundGroup(
    idRefundGroup: string,
    status: RefundStatus,
    failed: boolean,
    paymentInfo: PaymentInfoModel,
    type: number,
    brcodeType: BrcodeType,
    newAttempt: boolean
  ): Promise<void> {
    const dataNewAttempt = {
      Status: RefundStatus[status],
      Failed: failed,
      NewAttempt: newAttempt,
    };
    const dataWithoutNewAttempt = {
      status,
      failed,
      type,
      brcodeType,
      newAttempt,
      FinancialInstitutionCode: paymentInfo.FinancialInstitutionCode,
      BranchNumber: paymentInfo.BranchNumber,
      BranchDigit: paymentInfo.BranchDigit,
      AccountNumber: paymentInfo.AccountNumber,
      AccountDigit: paymentInfo.AccountDigit,
      AccountType: paymentInfo.AccountType,
      Email: paymentInfo.Email,
      Phone: paymentInfo.Phone,
      RandomKey: paymentInfo.RandomKey,
    };
    return await this.put<void, {}>(
      `${env.financialUri}/RefundGroup/${idRefundGroup}`,
      newAttempt ? dataNewAttempt : dataWithoutNewAttempt
    ).then(({ data }) => data);
  }
  public async refundInstallment(refundGroup: RefundGroupModel): Promise<void> {
    await this.post(`${env.financialUri}/RefundGroup/`, {
      Name: refundGroup.Name,
      ContractNumber: refundGroup.ContractNumber,
      Refunds: refundGroup.PaymentsInfo,
      Reason: refundGroup.Reason,
      MarkOff: refundGroup.MarkOff,
      AllRefundsExternal: refundGroup.AllRefundsExternal,
    });
  }
  public async allowanceInstallment(
    contractId: string,
    installmentId: string,
    descReason: string
  ): Promise<void> {
    await this.post(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}/accredit`,
      {},
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    );
  }
  public async paidByOtherWayInstallment(
    contractId: string,
    installmentId: string,
    descReason: string
  ): Promise<void> {
    await this.post(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}/paidByOtherWay`,
      {},
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    );
  }
  public async cancelInstallment(
    contractId: string,
    installmentId: string,
    descReason: string
  ): Promise<void> {
    await this.post(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}/cancel`,
      {},
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    );
  }

  public async getAttemptList(
    contractId: string,
    installmentId: string,
    limit = 999
  ): Promise<AttemptModel[]> {
    return await this.get<AttemptModel[]>(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}/attempts`,
      {
        params: { limit },
        headers: { tenantId: refsService.modules.company.account.Id },
      }
    ).then(({ data }) => data);
  }
  public async getTransactionList(
    contractId: string,
    installmentId: string,
    attemptId: string,
    limit = 999
  ): Promise<TransactionModel[]> {
    return await this.get<TransactionModel[]>(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}/attempts/${attemptId}/transactions`,
      {
        params: { limit },
        headers: { tenantId: refsService.modules.company.account.Id },
      }
    ).then(({ data }) => data);
  }

  public async resendBankSlip(
    contractId: string,
    installmentId: string,
    listEmails: string
  ): Promise<void> {
    await this.post(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}/resendBankSlip`,
      { listEmails }
    );
  }

  public async resendBRCode(
    contractId: string,
    installmentId: string,
    listEmails: string
  ): Promise<void> {
    await this.post(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}/resendBRCode`,
      { listEmails }
    );
  }
  public async abortRefundGroup(
    refundGroupId: string,
    reason: string
  ): Promise<void> {
    await this.post(`${env.financialUri}/RefundGroup/abort/${refundGroupId}`, {
      reason,
    });
  }

  public async loadContract(contractId: string): Promise<ContractModel> {
    return await this.get<ContractModel>(
      `${env.billingUri}/Contracts/${contractId}`
    ).then(({ data }) => data);
  }

  public async getContractsCustomerName(refundGroupList: RefundGroupModel[]) {
    refundGroupList.forEach(async (refundGroup) => {
      refundGroup.ContractName = refundGroup.Name;
      if (refundGroup.Refunds && refundGroup.Refunds.length > 0) {
        const contractRefundGroup = await this.getRefundGroup(refundGroup.Id);
        if (contractRefundGroup) {
          refundGroup.ContractName = contractRefundGroup.ContractName;
        }
      }
    });
  }

  public async generateLinkUpdate(
    contractId: string,
    installments: string[],
    updateContract: boolean,
    emails: string
  ): Promise<void> {
    await this.post(
      `${env.changerUri}/v3/paymentUpdates`,
      {
        ContractId: contractId,
        Installments: installments,
        UpdateContract: updateContract,
        Emails: emails,
      },
      { headers: { tenantId: refsService.modules.company.account.Id } }
    );
  }

  public async getRefundFile(refundId: string): Promise<Blob | undefined> {
    try {
      return await this.get<Blob | null>(
        `${env.financialUri}/Refund/${refundId}/files`,
        { responseType: 'blob' }
      )
        .then(({ data }) => new Blob([data as BlobPart]))
        .catch(null);
    } catch {
      return undefined;
    }
  }

  public async getPartialRefundByInstallmentId(
    installmentId: string[]
  ): Promise<StatementProcessorResponseModel[] | undefined> {
    return await this.post<StatementProcessorResponseModel[], object>(
      `${env.financialUri}/Statements/FindProcessorByInstallmentsId`,
      installmentId
    )
      .then(({ data }) => data)
      .catch((err) => err);
  }

  public async getRefundConfigs(): Promise<
    RefundSummaryResponseModel | undefined
  > {
    return await this.get<RefundSummaryResponseModel>(
      `${env.financialUri}/Settings/summary/tenant/` +
        refsService.modules.company.account.Id
    )
      .then(({ data }) => data)
      .catch((err) => err);
  }

  public async addContact(
    contractId: string,
    customerId: string,
    contact: ContactBillingModel[]
  ): Promise<void> {
    await this.post<void, ContactBillingModel[]>(
      `${env.billingUri}/Contracts/${contractId}/customers/${customerId}/contacts`,
      contact,
      { headers: { tenantId: refsService.modules.company.account.Id } }
    );
  }

  public async forceUpdateStatus(
    contractId: string,
    installmentId: string,
    data: {}
  ): Promise<void> {
    await this.post<void, any>(
      `${env.billingUri}/Contracts/${contractId}/installments/${installmentId}/force-status`,
      data
    );
  }

  public async getCampaignBlock(contractId: string): Promise<boolean> {
    return await this.get<boolean>(
      `${env.billingUri}/Contracts/${contractId}/campaignBlock`
    ).then(({ data }) => data);
  }

  public async createCampaignBlock(
    contractId: string
  ): Promise<void | boolean> {
    return await this.post<void, boolean>(
      `${env.billingUri}/Contracts/${contractId}/campaignBlock`
    ).then(({ data }) => data);
  }

  public async deleteCampaignBlock(contractId: string): Promise<boolean> {
    return await this.delete(
      `${env.billingUri}/Contracts/${contractId}/campaignBlock`
    ).then(({ data }) => data);
  }

  public async sendIntermediateEmail(
    contractId: string,
    installmentId: string
  ): Promise<{ ok: boolean; message: string }> {
    return await this.post<any, { ok: boolean; message: string }>(
      `${env.miniSiteUri}/Contracts/${contractId}/installments/${installmentId}`,
      undefined,
      { headers: { tenantId: refsService.modules.company.account.Id } }
    ).then(({ data }) => data);
  }

  public async getWebhookEventsByContractId(
    contractId: string,
    filter?: string
  ): Promise<WebhookEventModel[]> {
    return await this.get<WebhookEventModel[]>(
      `${env.billingUri}/Webhooks/events/contract/${contractId}`,
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
        },
        params: { filter },
      }
    ).then(({ data }) => {
      return data;
    });
  }

  public async resendWebhook(
    contractId: string,
    descReason: string,
    list: WebhookEventResendModel[]
  ): Promise<void> {
    return await this.post<void, WebhookEventResendModel[]>(
      `${env.billingUri}/Webhooks/events/${contractId}/resend`,
      list,
      {
        headers: {
          tenantId: refsService.modules.company.account.Id,
          ticketId: refsService.modules.ticket.ticket.Id,
          descReason: encodeURIComponent(descReason),
        },
      }
    ).then(({ data }) => {
      data;
    });
  }
}
